import { useCallback } from "react";
import type { UpdateProductArgs } from "@jasper/use-cases";
import type { ItemType } from "@jasper/client-models";
import type { BulkTeethSubmitOptions, ConfigurableProduct } from "../types";
import type { ToothFormat } from "@jasper/forms";

type BulkTeethSelection = {
  submissionOptions?: BulkTeethSubmitOptions;
  onSubmit: <T extends ToothFormat>(
    teeth: T[][],
    formatTeethSelection: (args: {
      teeth: T[];
      product: ConfigurableProduct;
      itemType: ItemType;
    }) => Promise<UpdateProductArgs> | UpdateProductArgs,
    options?: BulkTeethSubmitOptions
  ) => Promise<void>;
};

type UseTeethSelectionSubmissionArgs<T extends ToothFormat> = {
  currentGroupedTeeth: readonly T[][];
  isBulkSelection: boolean;
  isBulkMode: boolean;
  bulk?: BulkTeethSelection;
};

const compareTooth = <T extends ToothFormat>(toothA: T, toothB: T): boolean => {
  if (typeof toothA === "number" && typeof toothB === "number") {
    return toothA === toothB;
  }

  if (typeof toothA === "object" && typeof toothB === "object") {
    return toothA.tooth === toothB.tooth && toothA.value === toothB.value;
  }

  return false;
};

export const useTeethSelectionSubmission = <T extends ToothFormat>({
  currentGroupedTeeth,
  isBulkSelection,
  isBulkMode,
  bulk,
}: UseTeethSelectionSubmissionArgs<T>) =>
  useCallback(
    async (
      selectedGroupedTeeth: T[][],
      formatTeethSelection: (args: {
        teeth: T[];
        product: ConfigurableProduct;
        itemType: ItemType;
      }) => Promise<UpdateProductArgs> | UpdateProductArgs,
      onSave: () => Promise<void>
    ) => {
      const selectedSet = new Set(
        selectedGroupedTeeth
          .flat()
          .map(tooth => (typeof tooth === "number" ? tooth : tooth.tooth))
      );
      const previousSet = new Set(currentGroupedTeeth.flat());

      const areSameGroups =
        selectedGroupedTeeth.length === currentGroupedTeeth.length &&
        selectedGroupedTeeth.every((group, index) => {
          const previousGroup = currentGroupedTeeth[index];
          return (
            group.length === previousGroup.length &&
            group.every(tooth =>
              previousGroup.some(previousTooth =>
                compareTooth(previousTooth, tooth)
              )
            )
          );
        });

      if (areSameGroups && !isBulkSelection) {
        return;
      }

      if (
        bulk &&
        isBulkSelection &&
        (selectedGroupedTeeth.length > 1 || isBulkMode)
      ) {
        await bulk.onSubmit(
          selectedGroupedTeeth,
          formatTeethSelection,
          bulk.submissionOptions
        );
        return;
      }

      const removedTeeth = [...previousSet].filter(
        tooth =>
          !selectedSet.has(typeof tooth === "number" ? tooth : tooth.tooth)
      );

      if (!removedTeeth.length) {
        await onSave();
        return;
      }

      await onSave();
    },
    [bulk, currentGroupedTeeth, isBulkMode, isBulkSelection]
  );
